import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../../Common/DatasetSelector';
import DiscretizationLayout from '../../../Common/DiscretizationLayout';

const BarChart = ({ plotData, defaultTitle, titleKey }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => localStorage.getItem(`dwtq_title_${titleKey}`) || defaultTitle || '');
  const [showEditor, setShowEditor] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const renderPlot = (t) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const grades = plotData.map(d => d.grade);
    const traces = [
      { x: grades, y: plotData.map(d => d.LRA_all || 0), type: 'bar', name: 'All Periods', marker: { color: '#3498db' } },
      { x: grades, y: plotData.map(d => d.LRA_dwt || 0), type: 'bar', name: 'DWT Periods', marker: { color: '#e67e22' } },
      { x: grades, y: plotData.map(d => d.MoC_DWT || 0), type: 'bar', name: 'MoC DWT', marker: { color: '#e74c3c', opacity: 0.5 } }
    ];
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t, barmode: 'group', xaxis: { tickangle: -45, automargin: true },
      template: 'plotly_white', height: 450,
      margin: { t: 40, b: 120, l: 60, r: 20 },
      legend: { orientation: 'h', y: -0.3 }
    }, { responsive: true });
  };

  useEffect(() => { if (plotData && plotData.length > 0) renderPlot(title); }, [plotData, title]);
  if (!plotData || plotData.length === 0) return <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>No data available.</div>;

  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px' }}>{title}</h4>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => setShowTable(!showTable)} style={{ padding: '2px 8px', fontSize: '11px', background: '#34495e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{showTable ? 'Hide' : 'Table'}</button>
          <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>✏️</button>
        </div>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <button onClick={() => { renderPlot(title); localStorage.setItem(`dwtq_title_${titleKey}`, title); setShowEditor(false); }} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '450px' }}></div>
      {showTable && (
        <div style={{ marginTop: '12px', maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
              <tr>{Object.keys(plotData[0] || {}).map(h => <th key={h} style={{ padding: '6px' }}>{h}</th>)}</tr>
            </thead>
            <tbody>{plotData.map((row, i) => (<tr key={i}>{Object.keys(plotData[0] || {}).map(h => <td key={h} style={{ padding: '4px' }}>{typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}</td>)}</tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DwtQuantification = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(null);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [saveLraName, setSaveLraName] = useState('LRA_DWT');
  const [saveMocName, setSaveMocName] = useState('MoC_DWT');
  const [dwtValues, setDwtValues] = useState([]);
  const [dwtOptions, setDwtOptions] = useState([]);
  const [groups, setGroups] = useState({ segmentation: false, target: false, dwt: false });
  const [formData, setFormData] = useState({ Default_Date: null, Calibration_segment: null, Grade: null, Target_variable: null, dwt_column: null, dwt_values: [] });

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => { if (!window.Plotly) { const s = document.createElement('script'); s.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js'; document.head.appendChild(s); } }, []);

  const hdl = (data) => { setColumns(data.columns || []); setDatasetInfo(data); setDatasetLoaded(true); toast.success('Loaded'); };
  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));
  const toggleGroup = (g) => setGroups(p => ({ ...p, [g]: !p[g] }));

  const loadDwtValues = async () => {
    if (!formData.dwt_column || !datasetInfo?.id) return;
    try {
      const res = await axios.get(`/api/cart/column-values?dataset_id=${datasetInfo.id}&column=${formData.dwt_column.value}`);
      const vals = res.data.values || [];
      setDwtOptions(vals.map(v => ({ value: v, label: String(v) })));
    } catch (err) { console.error(err); }
  };
  useEffect(() => { if (formData.dwt_column) loadDwtValues(); }, [formData.dwt_column, datasetInfo]);

  const saveParams = async () => {
    if (!formData.Default_Date || !formData.Target_variable) { toast.error('Select Date and Target'); return; }
    if (!formData.Grade && !formData.Calibration_segment) { toast.error('Select Grade or Calibration'); return; }
    setLoading(true);
    try {
      const dwt_values_str = formData.dwt_values.map(v => v.value).join(',');
      await axios.post('/api/dwt-quantification/step0', {
        Default_Date: formData.Default_Date.value,
        Calibration_segment: formData.Calibration_segment?.value || '',
        Grade: formData.Grade?.value || '',
        Target_variable: formData.Target_variable.value,
        dwt_period: dwt_values_str
      });
      toast.success('Parameters saved'); setStep(1);
    } catch (err) { toast.error('Failed'); } finally { setLoading(false); }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const dwt_values_str = formData.dwt_values.map(v => v.value).join(',');
      const r = await axios.post('/api/dwt-quantification/step1', {
        Default_Date: formData.Default_Date.value,
        Calibration_segment: formData.Calibration_segment?.value || '',
        Grade: formData.Grade?.value || '',
        Target_variable: formData.Target_variable.value,
        dwt_period: dwt_values_str
      });
      setResults(r.data.results); toast.success(r.data.message); setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  const saveResults = async () => {
    if (!saveLraName.trim() || !saveMocName.trim()) { toast.error('Enter both column names'); return; }
    setLoadingStep('save');
    try {
      const r = await axios.post('/api/dwt-quantification/save-results', { lra_dwt_name: saveLraName.trim(), moc_dwt_name: saveMocName.trim() });
      toast.success(r.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setLoadingStep(null); }
  };

  const exportResults = async () => {
    try {
      const ct = { title: localStorage.getItem('dwtq_title_dwtq') || '' };
      const r = await axios.post('/api/dwt-quantification/export-with-titles', ct, { responseType: 'blob' }).catch(() => axios.get('/api/dwt-quantification/export', { responseType: 'blob' }));
      const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'dwtq_results.xlsx'; a.click(); window.URL.revokeObjectURL(u);
    } catch (err) { toast.error('Export failed'); }
  };

  const sc = (t) => { if (t === 0) setStep(0); else if (t === 1 && datasetLoaded) setStep(1); else if (t === 2 && results) setStep(2); };
  const cs = { control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }), menu: (b) => ({ ...b, zIndex: 9999 }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) };
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '14px' };
  const gcs = { padding: '16px', background: 'white' };

  return (
    <DiscretizationLayout title="Dwt Quantification" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      {step === 0 && (<>
        <DatasetSelector apiPrefix="/api/dwt-quantification" onDatasetLoaded={hdl} />
        {columns.length > 0 && (<div style={{ marginTop: '20px' }}>
          <div style={gs}><div style={ghs} onClick={() => toggleGroup('segmentation')}><span>🎯 1. Segmentation</span><span>{groups.segmentation ? '−' : '+'}</span></div>
            {groups.segmentation && <div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Calibration segment (optional)</label><Select options={columnOptions} value={formData.Calibration_segment} onChange={v => hfc('Calibration_segment', v)} isClearable styles={cs} menuPortalTarget={document.body} /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Grade (optional)</label><Select options={columnOptions} value={formData.Grade} onChange={v => hfc('Grade', v)} isClearable styles={cs} menuPortalTarget={document.body} /></div>
              </div>
              <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '8px' }}>⚠️ At least one required</p>
            </div>}
          </div>
          <div style={gs}><div style={ghs} onClick={() => toggleGroup('target')}><span>📊 2. Target and Date</span><span>{groups.target ? '−' : '+'}</span></div>
            {groups.target && <div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Default Date *</label><Select options={columnOptions} value={formData.Default_Date} onChange={v => hfc('Default_Date', v)} styles={cs} menuPortalTarget={document.body} /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Target Variable *</label><Select options={columnOptions} value={formData.Target_variable} onChange={v => hfc('Target_variable', v)} styles={cs} menuPortalTarget={document.body} /></div>
              </div>
            </div>}
          </div>
          <div style={gs}><div style={ghs} onClick={() => toggleGroup('dwt')}><span>📅 3. DWT Periods</span><span>{groups.dwt ? '−' : '+'}</span></div>
            {groups.dwt && <div style={gcs}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Select DWT Column</label>
                <Select options={columnOptions} value={formData.dwt_column} onChange={v => { hfc('dwt_column', v); setFormData(p => ({ ...p, dwt_values: [] })); }} styles={cs} menuPortalTarget={document.body} placeholder="Select column containing downturn periods..." />
              </div>
              {formData.dwt_column && dwtOptions.length > 0 && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Select Downturn Values</label>
                  <Select options={dwtOptions} isMulti value={formData.dwt_values} onChange={v => hfc('dwt_values', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select downturn years/values..." />
                </div>
              )}
            </div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button onClick={saveParams} disabled={loading} style={{ padding: '8px 28px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}>{loading ? 'Saving...' : 'Continue →'}</button>
          </div>
        </div>)}
      </>)}

      {step === 1 && (<div>
        <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Review Parameters</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
            <p><strong>Date:</strong> {formData.Default_Date?.label}</p>
            <p><strong>Target:</strong> {formData.Target_variable?.label}</p>
            <p><strong>Grade:</strong> {formData.Grade?.label || '-'}</p>
            <p><strong>Calibration:</strong> {formData.Calibration_segment?.label || '-'}</p>
            <p><strong>DWT Column:</strong> {formData.dwt_column?.label || '-'}</p>
            <p><strong>DWT Values:</strong> {formData.dwt_values?.map(v => v.label).join(', ') || '-'}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
          <button onClick={runAnalysis} disabled={loading} style={{ padding: '8px 24px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}>{loading ? 'Running...' : 'Run Analysis →'}</button>
        </div>
      </div>)}

      {step === 2 && results && (<div>
        <BarChart plotData={results.bar_data} defaultTitle="LRA: All vs DWT Periods" titleKey="dwtq" />
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div><label style={{ fontSize: '11px', fontWeight: 500 }}>LRA DWT Col</label><input type="text" value={saveLraName} onChange={e => setSaveLraName(e.target.value)} style={{ padding: '6px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', width: '130px' }} /></div>
            <div><label style={{ fontSize: '11px', fontWeight: 500 }}>MoC DWT Col</label><input type="text" value={saveMocName} onChange={e => setSaveMocName(e.target.value)} style={{ padding: '6px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', width: '130px' }} /></div>
            <button onClick={saveResults} disabled={loadingStep === 'save'} style={{ padding: '8px 20px', background: loadingStep === 'save' ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: loadingStep === 'save' ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>{loadingStep === 'save' ? '...' : '💾 Save'}</button>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={exportResults} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📥 Export</button>
          </div>
        </div>
      </div>)}
    </DiscretizationLayout>
  );
};

export default DwtQuantification;
